// CreateMemberSexualEnvironment.jsx
import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import cls from "./CreateMemberSexualEnvironment.module.scss";
import { SEXUAL_ENVIRONMENTS } from "@/enums/lifestyleEnums";
import { useAuth } from "@/app/auth-context-state";

/* Welcome-style hero placeholders (q14) */
import mrImg from "@/assets/UI/memberCreate/q14Mr.webp";
import mrsImg from "@/assets/UI/memberCreate/q14Mrs.webp";
import mrMImg from "@/assets/UI/memberCreate/q14MrM.webp";
import mrsMImg from "@/assets/UI/memberCreate/q14MrsM.webp";

/* ---- Framer animations (match AttractionGenders) ---- */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

/* Outlet context hook */
function useMemberLayoutContext() {
  return useOutletContext();
}

/* Emoji meta for sexual-environment options */
const ENV_META = {
  dim_lights_music: "🕯️",
  bedroom_comfort: "🛏️",
  shower_hot_tub: "💦",
  outdoor_spontaneous: "🌅",
  themed_events: "🎭",
};

/* Fallback English labels */
const ENV_LABELS = {
  dim_lights_music: "Dim lights, music & slow rhythm",
  bedroom_comfort: "Bedroom comfort with candles",
  shower_hot_tub: "Shower / hot tub intimacy",
  outdoor_spontaneous: "Outdoor or spontaneous play",
  themed_events: "Themed or lifestyle events",
};

function toggleChoice(current, value) {
  if (current.includes(value)) return current.filter((v) => v !== value);
  return [...current, value];
}

/* Closing text key helper – personalized by member’s gender */
function getClosingKey(rawGender, isCouple) {
  if (isCouple) return "onboarding.sexual_environment.closing.couple";
  if (rawGender === "man" || rawGender === "trans_man") {
    return "onboarding.sexual_environment.closing.man";
  }
  if (rawGender === "woman" || rawGender === "trans_woman") {
    return "onboarding.sexual_environment.closing.woman";
  }
  return "onboarding.sexual_environment.closing.other";
}

export default function CreateMemberSexualEnvironment() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const layoutCtx = useMemberLayoutContext() || {};
  const { memberType, isCouple: isCoupleCtx, rawGender } = layoutCtx;
  const isCouple = Boolean(isCoupleCtx || memberType === "couple");

  const [submitting, setSubmitting] = useState(false);
  const [memberChoices, setMemberChoices] = useState([]); // string[]
  const [partnerChoices, setPartnerChoices] = useState([]); // string[]

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const t = (k, o) => i18n.t(k, o);

  const title = t("onboarding.sexual_environment.title", {
    defaultValue: "Your favourite vibe",
  });

  const intro1 = t("onboarding.sexual_environment.intro1", {
    defaultValue:
      "Tell us the kind of setting that makes you feel the most turned on.",
  });
  const intro2 = t("onboarding.sexual_environment.intro2", {
    defaultValue: "You can pick more than one.",
  });
  const intro3 = t("onboarding.sexual_environment.intro3", {
    defaultValue: "This helps us match your feed to your real-life energy.",
  });

  const singleQuestion = t("onboarding.sexual_environment.single.question", {
    defaultValue: "What kind of environment gets you in the mood?",
  });

  const coupleLead = t("onboarding.sexual_environment.couple.lead", {
    defaultValue: "Pick for you, then let your partner pick too.",
  });
  const coupleDetail = t("onboarding.sexual_environment.couple.detail", {
    defaultValue:
      "Two people, two different triggers. We keep it respectful for both.",
  });

  const partnerLabel = t("onboarding.sexual_environment.partner.label", {
    defaultValue: "Now pick your partner’s favourite vibe",
  });

  const guideTitle = t("onboarding.sexual_environment.guide.title", {
    defaultValue: "Quick note",
  });
  const guideBody = t("onboarding.sexual_environment.guide.body", {
    defaultValue:
      "This doesn’t lock you in. It just helps your matches and suggestions feel more like your vibe.",
  });

  const baseOptions =
    Array.isArray(SEXUAL_ENVIRONMENTS) && SEXUAL_ENVIRONMENTS.length
      ? SEXUAL_ENVIRONMENTS
      : Object.keys(ENV_LABELS);

  const options = baseOptions.map((value) => ({
    value,
    emoji: ENV_META[value] || "✨",
    title: t(`onboarding.sexual_environment.options.${value}.label`, {
      defaultValue: ENV_LABELS[value] || value,
    }),
    hint: t(`onboarding.sexual_environment.tapToSelect.${value}`, {
      defaultValue: "Tap to add this vibe to your attraction mix",
    }),
  }));

  const hasMember = memberChoices.length > 0;
  const hasPartner = partnerChoices.length > 0;

  const closingKey =
    hasMember && (!isCouple || hasPartner)
      ? getClosingKey(rawGender, isCouple)
      : null;

  const closingText = closingKey ? t(closingKey, { defaultValue: "" }) : "";

  const submitDisabled =
    submitting || !memberChoices.length || (isCouple && !partnerChoices.length);

  async function onSubmit(e) {
    e.preventDefault();

    if (!memberChoices.length) {
      toast.info(
        t("onboarding.sexual_environment.toast.member_required", {
          defaultValue: "Pick at least one for you.",
        }),
      );
      return;
    }

    if (isCouple && !partnerChoices.length) {
      toast.info(
        t("onboarding.sexual_environment.toast.partner_required", {
          defaultValue: "Pick at least one for your partner.",
        }),
      );
      return;
    }

    const payload = isCouple
      ? {
          memberSexualEnvironment: memberChoices,
          partnerSexualEnvironment: partnerChoices,
        }
      : {
          memberSexualEnvironment: memberChoices,
        };

    setSubmitting(true);
    try {
      const { data } = await api.post(
        "/v1/member-create/sexual_environment",
        payload,
        { skipToast: true },
      );

      await refreshMe();
      navigate(data?.next || "/member/event_attract", { replace: true });
    } catch (err) {
      console.error("update sexual environment failed:", err);
      toast.error(
        t("onboarding.sexual_environment.toast.save_error", {
          defaultValue: "We couldn’t save this. Please try again.",
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  function renderOptionsBlock({ name, subtitleText, choices, setChoices }) {
    return (
      <div className={cls.block}>
        {subtitleText ? (
          <div className={cls.blockTitle}>{subtitleText}</div>
        ) : null}

        <fieldset className={cls.options}>
          <legend className="sr-only">{subtitleText || title}</legend>

          {options.map((opt) => {
            const checked = choices.includes(opt.value);

            return (
              <label
                key={`${name}-${opt.value}`}
                className={`${cls.option} ${checked ? cls.optionActive : ""}`}
              >
                <input
                  type="checkbox"
                  name={name}
                  value={opt.value}
                  checked={checked}
                  onChange={() =>
                    setChoices((prev) => toggleChoice(prev, opt.value))
                  }
                />

                <span className={cls.optionBody}>
                  <span className={cls.optionEmoji} aria-hidden="true">
                    {opt.emoji}
                  </span>

                  <span className={cls.optionText}>
                    <strong>{opt.title}</strong>
                    <small>{opt.hint}</small>
                  </span>

                  <span className={cls.optionDot} aria-hidden="true" />
                </span>
              </label>
            );
          })}
        </fieldset>
      </div>
    );
  }

  return (
    <main className={cls.page}>
      {/* Fixed background stage */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* Single-page scroll content (no nested scroll) */}
      <div className={cls.pageInner}>
        <div className={cls.formCard}>
          <div className={cls.formTop}>
            <div className={cls.formMeta}>
              <div className={cls.formTitle}>{title}</div>

              <div className={cls.formSub}>{intro1}</div>
              <div className={cls.formSub}>{intro2}</div>
              <div className={cls.formSub}>{intro3}</div>

              {!isCouple ? (
                <div className={cls.formSub} style={{ marginTop: 8 }}>
                  {singleQuestion}
                </div>
              ) : (
                <>
                  <div className={cls.formSub} style={{ marginTop: 8 }}>
                    {coupleLead}
                  </div>
                  <div className={cls.formSub}>{coupleDetail}</div>
                </>
              )}
            </div>
          </div>

          <form className={cls.form} onSubmit={onSubmit} noValidate>
            {renderOptionsBlock({
              name: "memberSexualEnvironment",
              subtitleText: singleQuestion,
              choices: memberChoices,
              setChoices: setMemberChoices,
            })}

            {isCouple &&
              renderOptionsBlock({
                name: "partnerSexualEnvironment",
                subtitleText: partnerLabel,
                choices: partnerChoices,
                setChoices: setPartnerChoices,
              })}

            <div className={cls.readNote}>
              <strong>💡 {guideTitle}</strong>
              <p>{guideBody}</p>
            </div>

            {closingText ? (
              <div className={cls.closingNote} aria-live="polite">
                {closingText.split("\n").map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            ) : null}

            <div className={cls.actions}>
              <button
                type="submit"
                className={cls.submit}
                disabled={submitDisabled}
                aria-busy={submitting ? "true" : "false"}
              >
                {submitting
                  ? t("common.saving", { defaultValue: "Saving…" })
                  : t("common.continue", { defaultValue: "Continue" })}
              </button>
            </div>
          </form>
        </div>

        <div className={cls.bottomSpace} />
      </div>
    </main>
  );
}
